import { Injectable } from '@angular/core';
import { HttpParams } from '@angular/common/http';
import { Observable, forkJoin } from 'rxjs';
import { first, map, switchMap, take } from 'rxjs/operators';
import { RoleUtils } from 'src/app/utils/role.utils';
import { environment } from 'src/environments/environment';
import { ApiService } from '../../shared/services/api.service';
import {
  GroupPermission,
  Permission,
  PermissionTableFunctionGroup,
} from '../model/permission';
import { CurrentUserService } from '../../shared/services/current-user.service';

const API_BASE_URL = environment.api.o3;

export enum PermissionsTableServiceRoutes {
  GROUPS = 'groups',
  PERMISSIONS = 'permissions',
  GROUP_PERMISSION = 'group-permission',
  GROUP_PERMISSION_DELETE = 'group-permission/delete',
}

@Injectable({ providedIn: 'root' })
export class PermissionsTableService {
  constructor(
    private apiService: ApiService,
    private currentUserService: CurrentUserService
  ) {}

  getGroupsWithKeys(
    application?: string,
    genericOnly?: boolean
  ): Observable<PermissionTableFunctionGroup[]> {
    let params = new HttpParams();
    if (application) {
      params = params.set('application', application);
    }
    if (genericOnly) {
      params = params.set('generic', 'true');
    }

    const url = params.toString()
      ? `${PermissionsTableServiceRoutes.GROUPS}?${params.toString()}`
      : PermissionsTableServiceRoutes.GROUPS;

    return this.apiService
      .get<PermissionTableFunctionGroup[]>(url, API_BASE_URL)
      .pipe(
        first(),
        switchMap((groups) =>
          this.currentUserService.currentUser.pipe(
            take(1),
            map((user) => {
              if (!user) {
                return [];
              }

              return groups.filter((group) => {
                const roleApplicationId = RoleUtils.extractApplicationFromRole(group.key);

                if (genericOnly) {
                  if (roleApplicationId !== null) return false;
                  if (application) return false;
                  return user.isUMAdmin;
                }

                if (application && roleApplicationId !== application) {
                  return false;
                }

                if (roleApplicationId === null) {
                  return user.isUMAdmin;
                }

                if (user.isUMAdmin) {
                  return true;
                }

                return user.canViewRole(group.key);
              });
            })
          )
        )
      );
  }

  getGroupsAndFunctions(
    application?: string,
    genericOnly?: boolean
  ): Observable<[Permission[], GroupPermission[]]> {
    let params = new HttpParams();
    if (application) {
      params = params.set('application', application);
    }
    if (genericOnly) {
      params = params.set('generic', 'true');
    }

    const url = params.toString()
      ? `permissions?${params.toString()}`
      : 'permissions';

    const permissions$ = this.apiService
      .get<Permission[]>(url, API_BASE_URL)
      .pipe(
        map((permissions) => {
          let filtered = permissions;

          if (application) {
            filtered = permissions.filter(
              (perm) => perm.application === application
            );
          }

          if (genericOnly) {
            filtered = permissions.filter((perm) => perm.application === null);
          }

          return filtered;
        })
      );

    return forkJoin([permissions$, this.getGroupsWithPermissions()]).pipe(
      first()
    );
  }

  getGroupsWithPermissions(): Observable<GroupPermission[]> {
    return this.apiService
      .get<GroupPermission[]>(
        PermissionsTableServiceRoutes.GROUP_PERMISSION,
        API_BASE_URL
      )
      .pipe(
        switchMap((groups) =>
          this.currentUserService.currentUser.pipe(
            take(1),
            map((user) => {
              if (!user) {
                return [];
              }

              if (user.isUMAdmin) {
                return groups;
              }

              return groups.filter((group) => {
                const roleApplicationId = RoleUtils.extractApplicationFromRole(group.groupKey);
                if (roleApplicationId === null) {
                  return false;
                }
                return user.applications.has(roleApplicationId);
              });
            })
          )
        )
      );
  }

  buildPost(permission: GroupPermission): Observable<any> {
    return this.apiService.post(
      PermissionsTableServiceRoutes.GROUP_PERMISSION,
      permission,
      API_BASE_URL
    );
  }

  buildDelete(permission: GroupPermission): Observable<any> {
    return this.apiService.delete(
      PermissionsTableServiceRoutes.GROUP_PERMISSION_DELETE,
      permission,
      API_BASE_URL
    );
  }
}
